// src/pages/InscripcionPOS.js
//
// Activación de HolaVeci Local para la empresa del usuario logueado.
// Aparece automáticamente cuando el ADMIN_NEGOCIO entra y su empresa aún
// no tiene Local activo.
//
// D-23 — HolaVeci Local exige pago antes de activarse. El backend
// (GET /api/pos/inscripcion/estado) dice en qué paso está el negocio:
//
//   DOCUMENTOS        solo Local: subir cédula (frente/reverso) y RUT.
//   EN_REVISION       solo Local: el validador revisa los documentos.
//   PAGAR_LOCAL       solo Local aprobado: pagar el plan con Wompi.
//   PAGAR_EN_APP      tiene HolaVeci Negocio: agrega Local desde la app (360°).
//   COMPLETAR_NEGOCIO tiene HolaVeci Negocio pero no ha terminado su registro.
//   ACTIVAR           plan pagado: datos fiscales + botón Activar habilitado.
//   ACTIVO            listo (el layout deja pasar al POS).
//   BLOQUEADO/SOPORTE aviso sin acciones.
//
// En PAGAR_LOCAL y PAGAR_EN_APP el formulario se ve, pero el botón Activar
// queda deshabilitado hasta que el pago se confirme.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Button, TextField,
  Alert, LinearProgress, Divider, Grid, Chip,
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApp } from '../context/AppContext';
import { inscripcionPOS as apiIns } from '../api/posApi';
import DocumentosLocal from '../components/holaveciLocal/DocumentosLocal';
import PagoLocalWompi from '../components/holaveciLocal/PagoLocalWompi';

const PASOS_SOLO_LOCAL = [
  { clave: 'DOCUMENTOS',  etiqueta: 'Documentos' },
  { clave: 'EN_REVISION', etiqueta: 'Revisión' },
  { clave: 'PAGAR_LOCAL', etiqueta: 'Pago' },
  { clave: 'ACTIVAR',     etiqueta: 'Activar' },
];

function LineaDePasos({ paso }) {
  const idx = PASOS_SOLO_LOCAL.findIndex(p => p.clave === paso);
  return (
    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
      {PASOS_SOLO_LOCAL.map((p, i) => (
        <Chip
          key={p.clave}
          size="small"
          label={`${i + 1}. ${p.etiqueta}`}
          color={i < idx ? 'success' : i === idx ? 'primary' : 'default'}
          variant={i <= idx ? 'filled' : 'outlined'}
        />
      ))}
    </Stack>
  );
}

const InscripcionPOS = ({ onActivado }) => {
  const { notify } = useApp();
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [g, setG] = useState(false);
  const [f, setF] = useState({
    nit: '', digitoVerificacion: '',
    razonSocial: '', nombreComercial: '',
    direccion: '', telefono: '', email: '',
    regimentTributarioId: '',
    tipoOrganizacionId: '',
  });

  const cargar = useCallback(async (prellenar = false) => {
    try {
      const r = await apiIns.estado();
      setEstado(r);
      if (r.contrataPOS && !r.bloqueadoPorMora) { onActivado?.(r); return; }
      if (prellenar) {
        setF(s => ({
          ...s,
          razonSocial: r.razonSocial && !r.razonSocial.startsWith('EMP-') ? r.razonSocial : '',
          nombreComercial: r.razonSocial && !r.razonSocial.startsWith('EMP-') ? r.razonSocial : '',
        }));
      }
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo verificar el estado.', 'error');
    } finally { setCargando(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { cargar(true); }, [cargar]);

  const submit = async () => {
    if (!estado?.puedeActivar) return;
    if (!f.nit.trim() || !f.digitoVerificacion.trim() || !f.razonSocial.trim() || !f.direccion.trim()) {
      notify('NIT, dígito de verificación, razón social y dirección son obligatorios.', 'error');
      return;
    }
    setG(true);
    try {
      const r = await apiIns.inscribir({
        nit: f.nit.trim(),
        digitoVerificacion: f.digitoVerificacion.trim(),
        razonSocial: f.razonSocial.trim(),
        nombreComercial: f.nombreComercial.trim() || null,
        direccion: f.direccion.trim(),
        telefono: f.telefono.trim() || null,
        email: f.email.trim() || null,
        regimentTributarioId: f.regimentTributarioId ? Number(f.regimentTributarioId) : null,
        tipoOrganizacionId:   f.tipoOrganizacionId   ? Number(f.tipoOrganizacionId)   : null,
      });
      notify(r.mensaje || 'HolaVeci Local activado.', 'success');
      if (onActivado) onActivado(r);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo activar HolaVeci Local.', 'error');
      if (e.status === 402) cargar();
    } finally { setG(false); }
  };

  if (cargando) return <LinearProgress />;
  if (!estado) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', py: 2 }}>
        <Alert severity="error" action={<Button onClick={() => cargar(true)}>Reintentar</Button>}>
          No se pudo consultar el estado de tu negocio.
        </Alert>
      </Box>
    );
  }
  if (estado.paso === 'PAGAR_MENSUALIDAD') {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', py: 2 }}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              <BlockIcon color="error" sx={{ fontSize: 44 }} />
              <Box>
                <Typography variant="h5" fontWeight={700}>Tu HolaVeci está suspendido</Typography>
                <Typography variant="body2" color="text.secondary">{estado.razonSocial}</Typography>
              </Box>
            </Stack>
            <Alert severity="error" sx={{ mb: 2 }}>
              No recibimos el pago de tu mensualidad a tiempo. Mientras esté pendiente no puedes usar
              HolaVeci Local ni la app HolaVeci Negocio, y tu tienda no aparece a los vecis.
              Paga ahora y se reactiva de inmediato.
            </Alert>
            <PagoLocalWompi
              modo="mensualidad"
              onPagado={() => { notify('Pago confirmado. ¡Tu HolaVeci está activo de nuevo!', 'success'); cargar(); }}
            />
          </CardContent>
        </Card>
      </Box>
    );
  }
  if (estado.contrataPOS) {
    return (
      <Alert severity="success" icon={<CheckCircleIcon />}>
        HolaVeci Local ya está activo desde{' '}
        {new Date(estado.fechaActivacionPOS).toLocaleDateString('es-CO')}.
      </Alert>
    );
  }

  const paso = estado.paso;
  const actualizar = (
    <Button size="small" startIcon={<RefreshIcon />} onClick={() => cargar()}>Actualizar</Button>
  );

  // ── Pasos sin formulario ─────────────────────────────────────
  const tarjeta = (contenido) => (
    <Box sx={{ maxWidth: 760, mx: 'auto', py: 2 }}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <PointOfSaleIcon color="primary" sx={{ fontSize: 44 }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>Activar HolaVeci Local</Typography>
              <Typography variant="body2" color="text.secondary">
                {estado.razonSocial}
              </Typography>
            </Box>
          </Stack>
          {estado.esSoloLocal && <LineaDePasos paso={paso} />}
          {contenido}
        </CardContent>
      </Card>
    </Box>
  );

  if (paso === 'DOCUMENTOS') {
    return tarjeta(
      <Stack spacing={2}>
        <Alert severity={estado.estadoValidacion === 'DOCS_RECHAZADOS' ? 'warning' : 'info'}>
          {estado.estadoValidacion === 'DOCS_RECHAZADOS'
            ? (estado.notasValidacion || 'Algunos documentos fueron rechazados. Súbelos de nuevo corregidos.')
            : 'Sube la cédula del representante y el RUT del negocio para validar tu cuenta.'}
        </Alert>
        <DocumentosLocal empresaId={estado.empresaId} onCambio={() => cargar()} />
      </Stack>
    );
  }

  if (paso === 'EN_REVISION') {
    return tarjeta(
      <Stack spacing={2}>
        <Alert severity="info" icon={<HourglassTopIcon />} action={actualizar}>
          Recibimos tus documentos. Estamos validando tu negocio; te avisaremos por correo cuando
          termine la revisión.
        </Alert>
        <DocumentosLocal empresaId={estado.empresaId} onCambio={() => cargar()} />
      </Stack>
    );
  }

  if (paso === 'COMPLETAR_NEGOCIO') {
    return tarjeta(
      <Alert severity="info" icon={<PhoneIphoneIcon />} action={actualizar}>
        Tu negocio está registrado en <b>HolaVeci Negocio</b> pero aún no termina su activación.
        Completa la validación y el pago en tu app HolaVeci Negocio; después podrás agregar
        HolaVeci Local desde la misma app.
      </Alert>
    );
  }

  if (paso === 'BLOQUEADO' || paso === 'SOPORTE') {
    return tarjeta(
      <Alert severity={paso === 'BLOQUEADO' ? 'error' : 'warning'} icon={<BlockIcon />}>
        {paso === 'BLOQUEADO'
          ? 'Tu negocio está suspendido o fue rechazado. Escríbenos a soporte@holaveci.com.'
          : 'No pudimos determinar el estado de tu negocio. Escríbenos a soporte@holaveci.com.'}
      </Alert>
    );
  }

  // ── Pasos con formulario: PAGAR_LOCAL, PAGAR_EN_APP, ACTIVAR ──
  const bloqueado = !estado.puedeActivar;

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', py: 2 }}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <PointOfSaleIcon color="primary" sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>Activar HolaVeci Local</Typography>
              <Typography variant="body2" color="text.secondary">
                Facturación mostrador con cajas, turnos, arqueo e inventario.
              </Typography>
            </Box>
          </Stack>

          {estado.esSoloLocal && <LineaDePasos paso={paso} />}

          {paso === 'PAGAR_LOCAL' && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                ¡Tu negocio fue aprobado! Paga tu plan para habilitar la activación.
              </Alert>
              <PagoLocalWompi onPagado={() => { notify('Pago confirmado. Ya puedes activar HolaVeci Local.', 'success'); cargar(); }} />
            </Box>
          )}

          {paso === 'PAGAR_EN_APP' && (
            <Alert severity="info" icon={<PhoneIphoneIcon />} sx={{ mb: 2 }} action={actualizar}>
              Tu negocio ya tiene <b>HolaVeci Negocio</b>. Para activar HolaVeci Local agrega el plan{' '}
              <b>HolaVeci 360°</b> desde tu app HolaVeci Negocio: <b>Perfil → Agregar HolaVeci Local</b>. Cuando el pago
              se confirme, aquí se habilita el botón Activar.
            </Alert>
          )}

          {paso === 'ACTIVAR' && estado.esSoloLocal && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
              Pago confirmado. Completa tus datos fiscales y activa HolaVeci Local.
            </Alert>
          )}

          {paso !== 'PAGAR_LOCAL' && !estado.esSoloLocal && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Tu empresa ya está en <b>HolaVeci</b>. Al activar <b>HolaVeci Local</b>{' '}
              complementas HolaVeci con facturación presencial. Completa tus
              datos fiscales para poder facturar en regla.
            </Alert>
          )}

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Datos fiscales
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField required fullWidth label="NIT" placeholder="900123456"
                         value={f.nit}
                         onChange={(e) => setF(s => ({ ...s, nit: e.target.value.replace(/\D/g, '') }))} />
            </Grid>
            <Grid item xs={4}>
              <TextField required fullWidth label="Dígito verif." placeholder="7"
                         value={f.digitoVerificacion} inputProps={{ maxLength: 1 }}
                         onChange={(e) => setF(s => ({ ...s, digitoVerificacion: e.target.value.replace(/\D/g, '') }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth label="Razón social"
                         helperText="Nombre oficial registrado ante la DIAN"
                         value={f.razonSocial}
                         onChange={(e) => setF(s => ({ ...s, razonSocial: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Nombre comercial (opcional)"
                         helperText="Como aparece en tu fachada o publicidad"
                         value={f.nombreComercial}
                         onChange={(e) => setF(s => ({ ...s, nombreComercial: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth label="Dirección fiscal"
                         value={f.direccion}
                         onChange={(e) => setF(s => ({ ...s, direccion: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Teléfono"
                         value={f.telefono}
                         onChange={(e) => setF(s => ({ ...s, telefono: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="email" label="Correo fiscal"
                         value={f.email}
                         onChange={(e) => setF(s => ({ ...s, email: e.target.value }))} />
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Podrás completar tipo de organización, régimen tributario y demás
            desde Configuración una vez activado HolaVeci Local.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Button fullWidth size="large" variant="contained"
                  onClick={submit} disabled={g || bloqueado}>
            {g ? 'Activando…' : 'Activar HolaVeci Local'}
          </Button>
          {bloqueado && (
            <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 1 }}>
              El botón se habilita cuando tu plan esté pago.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default InscripcionPOS;
